/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CoreApiService } from '../core-api-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { deleteFirmware } from '../fn/firmwares/delete-firmware';
import { DeleteFirmware$Params } from '../fn/firmwares/delete-firmware';
import { FirmwareDto } from '../models/firmware-dto';
import { getAllFirmwares } from '../fn/firmwares/get-all-firmwares';
import { GetAllFirmwares$Params } from '../fn/firmwares/get-all-firmwares';
import { getFirmwareById } from '../fn/firmwares/get-firmware-by-id';
import { GetFirmwareById$Params } from '../fn/firmwares/get-firmware-by-id';
import { PageDto } from '../models/page-dto';
import { releaseFirmware } from '../fn/firmwares/release-firmware';
import { ReleaseFirmware$Params } from '../fn/firmwares/release-firmware';
import { uploadFirmware } from '../fn/firmwares/upload-firmware';
import { UploadFirmware$Params } from '../fn/firmwares/upload-firmware';

@Injectable({ providedIn: 'root' })
export class FirmwaresAPI extends CoreApiService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllFirmwares()` */
  static readonly GetAllFirmwaresPath = '/firmwares';

  /**
   * List all firmwares.
   *
   * Returns a list of all firmwares. The request is paginated and returns per default only a subset of all firmwares
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllFirmwares()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllFirmwares$Response(params?: GetAllFirmwares$Params, context?: HttpContext): Observable<StrictHttpResponse<PageDto>> {
    return getAllFirmwares(this.http, this.rootUrl, params, context);
  }

  /**
   * List all firmwares.
   *
   * Returns a list of all firmwares. The request is paginated and returns per default only a subset of all firmwares
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllFirmwares$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllFirmwares(params?: GetAllFirmwares$Params, context?: HttpContext): Observable<PageDto> {
    return this.getAllFirmwares$Response(params, context).pipe(
      map((r: StrictHttpResponse<PageDto>): PageDto => r.body)
    );
  }

  /** Path part for operation `uploadFirmware()` */
  static readonly UploadFirmwarePath = '/firmwares';

  /**
   * Upload a new firmware.
   *
   * Upload a new firmware. Every firmware version can only be stored once
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadFirmware()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadFirmware$Response(params?: UploadFirmware$Params, context?: HttpContext): Observable<StrictHttpResponse<number>> {
    return uploadFirmware(this.http, this.rootUrl, params, context);
  }

  /**
   * Upload a new firmware.
   *
   * Upload a new firmware. Every firmware version can only be stored once
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadFirmware$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadFirmware(params?: UploadFirmware$Params, context?: HttpContext): Observable<number> {
    return this.uploadFirmware$Response(params, context).pipe(
      map((r: StrictHttpResponse<number>): number => r.body)
    );
  }

  /** Path part for operation `getFirmwareById()` */
  static readonly GetFirmwareByIdPath = '/firmwares/{id}';

  /**
   * Returns a specific firmware.
   *
   * Returns the firmware belonging on the given ID
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getFirmwareById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFirmwareById$Response(params: GetFirmwareById$Params, context?: HttpContext): Observable<StrictHttpResponse<FirmwareDto>> {
    return getFirmwareById(this.http, this.rootUrl, params, context);
  }

  /**
   * Returns a specific firmware.
   *
   * Returns the firmware belonging on the given ID
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getFirmwareById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFirmwareById(params: GetFirmwareById$Params, context?: HttpContext): Observable<FirmwareDto> {
    return this.getFirmwareById$Response(params, context).pipe(
      map((r: StrictHttpResponse<FirmwareDto>): FirmwareDto => r.body)
    );
  }

  /** Path part for operation `deleteFirmware()` */
  static readonly DeleteFirmwarePath = '/firmwares/{id}';

  /**
   * Delete a single firmware.
   *
   * Delete a firmware by it's unique identifier
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteFirmware()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteFirmware$Response(params: DeleteFirmware$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return deleteFirmware(this.http, this.rootUrl, params, context);
  }

  /**
   * Delete a single firmware.
   *
   * Delete a firmware by it's unique identifier
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteFirmware$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteFirmware(params: DeleteFirmware$Params, context?: HttpContext): Observable<void> {
    return this.deleteFirmware$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `releaseFirmware()` */
  static readonly ReleaseFirmwarePath = '/firmwares/{id}/releases';

  /**
   * Release a firmware.
   *
   * Release a single firmware for further distribution to other routers
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `releaseFirmware()` instead.
   *
   * This method doesn't expect any request body.
   */
  releaseFirmware$Response(params: ReleaseFirmware$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return releaseFirmware(this.http, this.rootUrl, params, context);
  }

  /**
   * Release a firmware.
   *
   * Release a single firmware for further distribution to other routers
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `releaseFirmware$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  releaseFirmware(params: ReleaseFirmware$Params, context?: HttpContext): Observable<void> {
    return this.releaseFirmware$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

}
